import React, { useEffect, useMemo, useState } from 'react';
import useAppState from '../../hooks/useAppState';
import styles from '../App/App.module.css';
import Race from '../Race/Race';
import Error from '../Error/Error';
import axios from 'axios';
import useNextToJump from '../../hooks/useNextToJump';
import clsx from 'clsx';

type Config = {
  scale: number;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  paddingBottom: number;
};

function App() {
  const { appParams, fourWaySplit, controllerLoadStatus } = useAppState();
  const [config, setConfig] = useState<Config>({
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    scale: 1,
  });

  const paramList = useMemo<
    { channelNumber: number; controllerId: string }[]
  >(() => {

    if (fourWaySplit) {
      const offset = 4 * +(appParams.screenNumber === 2);
      const arr = [...Array(4)];
      const split = arr.map((_, i) => {
        return {
          channelNumber: i + offset + 1,
          controllerId: appParams.computerName
        }
      });

      return split;
    }

    return [
      {
        controllerId: appParams.computerName,
        channelNumber: appParams.screenNumber,
      },
    ];
  }, [appParams.computerName, appParams.screenNumber, fourWaySplit]);

  const {
    meeting,
    nextToJump,
    getN2j: calculateNextToJump,
    isMultipleMeetings
  } = useNextToJump(
    paramList[0].controllerId,
    paramList[0].channelNumber,
    !paramList[0]
  );

  const {
    meeting: meeting2,
    nextToJump: nextToJump2,
    getN2j: calculateNextToJump2,
    isMultipleMeetings: isMultipleMeetings2
  } = useNextToJump(
    paramList[1]?.controllerId,
    paramList[1]?.channelNumber,
    !paramList[1]
  );

  const {
    meeting: meeting3,
    nextToJump: nextToJump3,
    getN2j: calculateNextToJump3,
    isMultipleMeetings: isMultipleMeetings3
  } = useNextToJump(
    paramList[2]?.controllerId,
    paramList[2]?.channelNumber,
    !paramList[2]
  );

  const {
    meeting: meeting4,
    nextToJump: nextToJump4,
    getN2j: calculateNextToJump4,
    isMultipleMeetings: isMultipleMeetings4
  } = useNextToJump(
    paramList[3]?.controllerId,
    paramList[3]?.channelNumber,
    !paramList[3]
  );

  useEffect(() => {
    if (appParams.screenNumber && appParams.computerName) {
      axios
        .post(
          'https://api.lfi.gcpprodau.tbm.sh/srv.lfi.LFIService/GetControllerConfig',
          {
            controllerId: appParams.computerName,
            channelNumber: appParams.screenNumber,
          },
          {
            headers: {
              Authorization: 'Key bGZpcHVibGlja2V5',
            },
          }
        )
        .then((res) => {
          setConfig(res.data?.controllerConfig ?? {});
        })
        .catch((err) => {});
    }
  }, [appParams.computerName, appParams.screenNumber]);

  // Apply the transform property for scaling and padding properties
  const appScale = useMemo(
    () => ({
      transform: `scale(${config.scale || 1}, ${config.scale || 1})`,
      paddingLeft: `${config.paddingLeft || 0}px`,
      paddingRight: `${config.paddingRight || 0}px`,
      paddingTop: `${config.paddingTop || 0}px`,
      paddingBottom: `${config.paddingBottom || 0}px`,
    }),
    [config]
  );

  if (!appParams.computerName || !appParams.screenNumber) return <Error />;

  if (controllerLoadStatus === 'error') {
    return (
      <div className={styles.appContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', fontSize: '2rem' }}>
        <div>Controller config failed ({appParams.computerName}). Check Network/console and that the controller exists in the LFI API.</div>
      </div>
    );
  }
  if (controllerLoadStatus === 'empty') {
    return (
      <div className={styles.appContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', fontSize: '2rem' }}>
        <div>No controller config for {appParams.computerName}. Assign channels in admin and ensure the controller exists.</div>
      </div>
    );
  }

  return (
    <div className={styles.appContainer}>
      <div className={clsx(styles.app, {[styles.grid]: paramList.length > 1})} style={appScale}>
        <Race
          nextToJump={nextToJump}
          calculateNextToJump={calculateNextToJump}
          meeting={meeting}
          isMultiMeeting={isMultipleMeetings}
        />
        {
          paramList[1] && (
            <Race
              nextToJump={nextToJump2}
              calculateNextToJump={calculateNextToJump2}
              meeting={meeting2}
              isMultiMeeting={isMultipleMeetings2}
            />
          )
        }
        {
          paramList[2] && (
            <Race
              nextToJump={nextToJump3}
              calculateNextToJump={calculateNextToJump3}
              meeting={meeting3}
              isMultiMeeting={isMultipleMeetings3}
            />
          )
        }
        {
          paramList[3] && (
            <Race
              nextToJump={nextToJump4}
              calculateNextToJump={calculateNextToJump4}
              meeting={meeting4}
              isMultiMeeting={isMultipleMeetings4}
            />
          )
        }
      </div>
    </div>
  );
}

export default App;
